<template>
  <Drawer
    v-model:visible="drawerVisible"
    :position="position"
    :class="drawerClass"
    :style="drawerStyle"
    :header="header"
    @hide="$emit('hide')"
  >
    <template #header v-if="showHeader">
      <div class="flex flex-column gap-3">
        <div class="flex align-items-center gap-3">
          <div>
            <h3 class="m-0 text-xl font-semibold">{{ personName }}</h3>
            <p v-if="idNumber" class="m-0 text-500 text-sm mt-1">ID: {{ idNumber }}</p>
          </div>
        </div>
        <div v-if="person?.photoIdUrl" class="flex justify-content-center">
          <img
            :src="person.photoIdUrl"
            alt="ID Photo"
            class="border-round shadow-1"
            style="max-height: 200px; max-width: 300px; object-fit: cover"
          />
        </div>
      </div>
    </template>

    <div v-if="person" class="space-y-6">
      <!-- Contact Information -->
      <div>
        <h4 class="text-lg font-semibold mb-3">Contact Information</h4>
        <div class="flex flex-column gap-3">
          <div>
            <div class="text-500 mb-1">Email</div>
            <div class="text-900 font-medium">{{ personEmail || 'N/A' }}</div>
          </div>
          <div>
            <div class="text-500 mb-1">Phone</div>
            <div class="text-900 font-medium">{{ personPhone || 'N/A' }}</div>
          </div>
          <div>
            <div class="text-500 mb-1">Address</div>
            <div class="text-900 font-medium">{{ personAddress || 'N/A' }}</div>
          </div>
          <div v-if="idNumber">
            <div class="text-500 mb-1">ID Number</div>
            <div class="text-900 font-medium">{{ idNumber }}</div>
          </div>
        </div>
      </div>

      <!-- Attachments -->
      <div v-if="person?.attachments && person.attachments.length > 0">
        <h4 class="text-lg font-semibold mb-3">Additional Documents</h4>
        <div class="grid">
          <div
            v-for="attachment in person.attachments"
            :key="attachment.id"
            class="col-12 md:col-6 mb-3"
          >
            <img
              :src="attachment.url"
              alt="Additional Document"
              class="border-round shadow-1 w-full cursor-pointer"
              style="max-height: 200px; object-fit: cover"
              @click="viewAttachment(attachment)"
            />
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div v-if="person?.notes">
        <h4 class="text-lg font-semibold mb-3">Notes</h4>
        <div class="p-3 surface-100 border-round">
          {{ person.notes }}
        </div>
      </div>
    </div>

    <div v-else class="flex flex-column align-items-center py-5">
      <i class="pi pi-user text-6xl text-500 mb-4"></i>
      <p class="text-xl">Person not found</p>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Drawer from 'primevue/drawer'
import Avatar from 'primevue/avatar'
import type { Person } from '@/types/Person'

interface Props {
  visible: boolean
  person: Person | null
  position?: 'left' | 'right' | 'top' | 'bottom' | 'full'
  size?: string
  header?: string
  showHeader?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  position: 'right',
  showHeader: true,
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  hide: []
}>()

// Computed property for v-model
const drawerVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
})

// Computed properties
const personName = computed(() => {
  if (!props.person) return 'Unknown'
  const { firstName, middleName, lastName } = props.person
  return [firstName, middleName, lastName].filter(Boolean).join(' ') || 'Unknown'
})

const personEmail = computed(() => {
  return props.person?.email || null
})

const personPhone = computed(() => {
  return props.person?.phone || null
})

const personAddress = computed(() => {
  if (!props.person) return null
  const { streetAddress, city, state, postalCode, country } = props.person
  const parts = [streetAddress, city, state, postalCode, country]
  return parts.filter(Boolean).join(', ')
})

const idNumber = computed(() => {
  return props.person?.idNumber || null
})

const drawerClass = computed(() => {
  const classes = []
  if (props.size) {
    classes.push(props.size)
  }
  return classes.join(' ')
})

const drawerStyle = computed(() => {
  if (props.position === 'full') {
    return { width: '100vw', height: '100vh' }
  }
  return {}
})

// Methods
function viewAttachment(attachment: { url: string }) {
  window.open(attachment.url, '_blank')
}
</script>

<style scoped>
.space-y-6 > * + * {
  margin-top: 1.5rem;
}
</style>
